import { useNavigation } from '@react-navigation/native';
import { Headline1, Meta, Spinner, theme } from 'aimo-ui';
import ParkingContext from 'context/ParkingContext';
import useChargingStation from 'hooks/useChargingStation';
import useCreateSession from 'hooks/useCreateSession';
import useHasParkingSession from 'hooks/useHasParkingSession';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet } from 'react-native';
import { useParkingStore } from 'stores/parkingStore';
import { getParkzoneState } from 'stores/parkzoneStore';
import styled from 'styled-components/native';
import { RowNoAlign, StyledWrapper } from 'styles/ContainerStyles';
import { Charger } from 'types/common';
import { ParkNavProps } from 'types/navigation';
import PublicDisplayId from './PublicDisplayId';

const ImageContainer = styled.View`
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const BGWrapper = styled.View`
  flex: 1;
  max-height: 150px;
  min-height: 100px;
  background-color: ${(p) => p.theme.colors.lightBg.yellow};
  padding: 25px 30px;
  border-radius: 8px;
`;

const InfoText = styled(Meta)`
  font-weight: 500;
  margin-top: 24px;
`;

const BottomMargin = styled.View<{ addMargin: boolean }>`
  margin-bottom: ${({ addMargin }) =>
    addMargin
      ? theme.normalising.heightPixel(250)
      : theme.normalising.heightPixel(150)}px;
`;

const styles = StyleSheet.create({
  imageCharger: {
    width: 87,
    height: 87,
    alignSelf: 'center',
  },
  imageCar: {
    width: 102,
    height: 67,
    alignSelf: 'center',
  },
});

export const ConnectCharger: React.FC = () => {
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const { t } = useTranslation();
  // Snapshot at mount, deliberately not a subscription: this feeds
  // useCreateSession's parkingZoneType below, so it must hold the tab the user
  // arrived on even if the tab is cleared later in the flow.
  const selectedTab = useRef(getParkzoneState().selectedParkingZoneTab);

  const { parkingZoneResult: parkingZone, selectedEvCharger } =
    useContext(ParkingContext);

  const { selectedParkingVehiclePlate } = useParkingStore((state) => state);

  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const { uid = '' } = parkingZone?.data?.parkZone || {};

  const { createParking, createCharging } = useCreateSession({
    parkingZoneType:
      selectedTab.current === 'PARK_AND_CHARGE'
        ? 'PARK_AND_CHARGE'
        : 'CHARGING',
    onErrorCallback: () => navigatePark('park'),
  });

  const { id: selectedChargerId, publicDisplayId } =
    selectedEvCharger || ({} as Charger);

  const ongoingParkingType = useParkingStore(
    (state) => state.selectedOngoingSession
  );
  const { hasParkingSession } = useHasParkingSession({
    parkingZoneUid: uid,
    vehicle: selectedVehicle,
  });

  const memoizedHasParkingSession = useRef(hasParkingSession);

  const {
    data: stationData,
    startPolling,
    stopPolling,
  } = useChargingStation(selectedChargerId);
  const stationOccupied = stationData?.readChargingStationV2?.evses?.some(
    (evse) =>
      evse.publicDisplayId === publicDisplayId && evse.status === 'OCCUPIED'
  );

  useEffect(() => {
    !stationOccupied ? startPolling(3000) : stopPolling();
    return () => stopPolling();
  }, [stationOccupied, startPolling, stopPolling]);

  // Fire-once guard: once the charger is connected we start the session exactly
  // once. This is defence-in-depth: useCreateSession has a module-level in-flight
  // guard against inter-instance duplicates (e.g., two StartParkingButton instances),
  // but this per-component guard protects against re-firing on re-renders when
  // `stationOccupied` briefly flickers.
  const hasStartedSessionRef = useRef(false);
  useEffect(() => {
    if (stationOccupied && !hasStartedSessionRef.current) {
      hasStartedSessionRef.current = true;
      (async () => {
        !memoizedHasParkingSession.current &&
        selectedTab.current === 'PARK_AND_CHARGE'
          ? await createParking()
          : await createCharging();
      })();
    }
  }, [stationOccupied, createCharging, createParking]);

  return (
    <>
      <StyledWrapper>
        <RowNoAlign>
          <PublicDisplayId
            isAvailable={true}
            publicDisplayId={publicDisplayId}
            testID="evcharger"
          />
        </RowNoAlign>
      </StyledWrapper>
      <StyledWrapper>
        <Headline1 center>
          {`${t('evCharging.connectChargerToYourCar')}...`}
        </Headline1>
      </StyledWrapper>
      <BGWrapper>
        <ImageContainer>
          <Image
            testID="car-image"
            style={styles.imageCar}
            source={require('assets/images/ev-car.png')}
          />
          <Spinner />
          <Image
            testID="charger-image"
            style={styles.imageCharger}
            source={require('assets/images/charger.png')}
          />
          <></>
        </ImageContainer>
      </BGWrapper>
      <InfoText center color={theme.colors.gray300}>
        {t('evCharging.connectChargerInfo')}
      </InfoText>
      <BottomMargin addMargin={ongoingParkingType !== ''} />
    </>
  );
};

export default ConnectCharger;
